import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Hls from "hls.js";

const isHlsUrl = (url) =>
  typeof url === "string" && url.split("?")[0].toLowerCase().endsWith(".m3u8");

/**
 * Plays post videos inside a dialog. Post media is usually an HLS playlist
 * (master.m3u8) which Chrome/Edge/Firefox cannot play natively, so HLS goes
 * through hls.js; Safari and plain mp4/webm use the native <video> element.
 * When playback fails and a fallbackUrl (the original pre-HLS file) is
 * provided, it retries once with that before surfacing an error.
 */
const VideoPlayerDialog = ({
  open,
  onClose,
  url,
  fallbackUrl,
  title = "Video",
  isDisabled = false,
}) => {
  const videoRef = useRef(null);
  const [activeUrl, setActiveUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setActiveUrl(url || "");
      setError("");
    }
  }, [open, url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!open || !video || !activeUrl) return undefined;

    let failed = false;
    const handlePlaybackFailure = () => {
      if (failed) return;
      failed = true;
      if (fallbackUrl && fallbackUrl !== activeUrl) {
        setActiveUrl(fallbackUrl);
      } else {
        setError(
          "This video could not be loaded. The file may be missing or still processing."
        );
      }
    };

    let hls = null;
    const handleNativeError = () => handlePlaybackFailure();

    if (isHlsUrl(activeUrl) && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(activeUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) {
          hls.destroy();
          hls = null;
          handlePlaybackFailure();
        }
      });
    } else {
      // Safari plays HLS natively; mp4/webm play natively everywhere.
      video.src = activeUrl;
      video.addEventListener("error", handleNativeError);
    }

    return () => {
      if (hls) hls.destroy();
      video.removeEventListener("error", handleNativeError);
      video.removeAttribute("src");
      video.load();
    };
  }, [open, activeUrl, fallbackUrl]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            minWidth: 0,
          }}
        >
          <Typography noWrap sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {isDisabled && <Chip label="Disabled" color="error" size="small" />}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "0 16px 16px" }}>
        {error ? (
          <Typography
            color="error"
            sx={{ padding: "24px 0", textAlign: "center" }}
          >
            {error}
          </Typography>
        ) : (
          <video
            ref={videoRef}
            controls
            autoPlay
            playsInline
            style={{
              width: "100%",
              maxHeight: "70vh",
              borderRadius: 8,
              backgroundColor: "#000",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoPlayerDialog;
